import { useEffect, useState } from 'react'
import confetti from 'canvas-confetti'
import type { NewlyAwardedBadge } from '@/types'

interface BadgeUnlockModalProps {
  badge: NewlyAwardedBadge
  onClose: () => void
}

export function BadgeUnlockModal({ badge, onClose }: BadgeUnlockModalProps) {
  const [showTrophy, setShowTrophy] = useState(false)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden'

    // Phase 1: Afficher le trophée
    const trophyTimer = setTimeout(() => setShowTrophy(true), 300)

    // Phase 2: Transition vers le badge avec confettis
    const badgeTimer = setTimeout(() => {
      setShowBadge(true)
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 },
        colors: ['#0F352D', '#1d5c4d', '#FFF198', '#feee80', '#fcf5cc']
      })
    }, 1200)

    return () => {
      document.body.style.overflow = ''
      clearTimeout(trophyTimer)
      clearTimeout(badgeTimer)
    }
  }, [])

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <style>{`
        @keyframes fireFlicker {
          0%, 100% {
            transform: scaleY(1) scaleX(1);
            opacity: 1;
          }
          25% {
            transform: scaleY(1.1) scaleX(0.95);
          }
          50% {
            transform: scaleY(0.9) scaleX(1.05);
            opacity: 0.9;
          }
          75% {
            transform: scaleY(1.05) scaleX(0.98);
          }
        }
        @keyframes trophyRise {
          0% {
            transform: translateY(100px) scale(0);
            opacity: 0;
          }
          60% {
            transform: translateY(-20px) scale(1.1);
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 30px rgba(255, 241, 152, 0.5);
          }
          50% {
            box-shadow: 0 0 60px rgba(255, 241, 152, 0.8);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .fire-flicker {
          animation: fireFlicker 0.3s ease-in-out infinite;
        }
        .trophy-rise {
          animation: trophyRise 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .glow {
          animation: glow 2s ease-in-out infinite;
        }
        .fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>

      {/* Overlay sombre */}
      <div className="absolute inset-0 bg-tessan-green/95 fade-in" />

      {/* Contenu */}
      <div
        className="relative z-10 flex flex-col items-center justify-center p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Flammes de fond */}
        {showTrophy && (
          <div className="absolute inset-x-0 bottom-20 flex items-end justify-center pointer-events-none">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="fire-flicker mx-1"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div
                  className="w-12 bg-gradient-to-t from-tessan-yellow via-tessan-cream to-transparent rounded-full blur-sm"
                  style={{ height: `${80 + Math.random() * 40}px`, opacity: 0.6 }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Trophée / Badge */}
        <div className={`relative z-10 ${showTrophy ? 'trophy-rise' : 'opacity-0'}`}>
          {!showBadge ? (
            <div className="text-6xl sm:text-8xl">🏆</div>
          ) : (
            <div className="glow w-40 h-40 sm:w-48 sm:h-48 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>
          )}
        </div>

        {/* Texte */}
        <div className={`mt-8 text-center transition-all duration-500 ${showBadge ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow mb-2">
            NOUVEAU BADGE !
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-tessan-cream">{badge.name}</div>
          {badge.points_reward > 0 && (
            <div className="text-lg sm:text-xl text-tessan-yellow mt-2">+{badge.points_reward} points</div>
          )}
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs text-sm sm:text-base">
            {badge.description}
          </div>
        </div>

        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className={`mt-8 px-6 py-3 bg-tessan-yellow text-tessan-green font-semibold rounded-lg
            hover:bg-tessan-cream transition-all duration-300 transform hover:scale-105
            ${showBadge ? 'opacity-100' : 'opacity-0'}`}
        >
          Super !
        </button>
      </div>
    </div>
  )
}
